/**
 * SiteModern - FULLY MIGRATED to use CRUDPage template with integrated map
 * 
 * Manages site/location data with Mapbox integration directly in the form dialog.
 * Map editing is now embedded within Add/Edit dialogs.
 */

import React, { useState, useEffect } from 'react';
import { Box, Chip, Button as MuiButton, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Typography, Table, TableHead, TableBody, TableRow, TableCell, TextField, Tooltip, Tabs, Tab, Alert, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch } from '@mui/material';
import { MapPin, Eye, X, Edit, Trash2, Layers, Plus, ClipboardCheck, ListTodo /*, Grid3x3 */ } from 'lucide-react';
import SiteAssessmentDialog from '../Components/Site/SiteAssessmentDialog';
import CreateTaskDialog from '../Components/Task/CreateTaskDialog';
import wellknown from 'wellknown';
import * as turf from '@turf/turf';

import CRUDPage from '../Components/UI/CRUDPage';
import ActionMenu from '../Components/UI/ActionMenu';
import MapboxMap from '../Components/MapboxMap';
import MapDrawField from '../Components/Forms/MapDrawField';
import { useOrg } from '../contexts/OrgContext';
import { usePermissions } from '../contexts/PermissionContext';
import BASE_URL from '../config';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// // Helper to calculate area from WKT polygon (used by Generate Grids feature)
// const calculateAreaFromWKT = (wktString) => {
//   try {
//     const geojson = wellknown.parse(wktString);
//     if (geojson && geojson.coordinates && geojson.coordinates[0]) {
//       const coords = geojson.coordinates[0];
//       let area = 0;
//       for (let i = 0; i < coords.length - 1; i++) {
//         area += coords[i][0] * coords[i + 1][1] - coords[i + 1][0] * coords[i][1];
//       }
//       area = Math.abs(area / 2);
//       const areaInSquareMeters = area * 111320 * 111320 * Math.cos((coords[0][1] * Math.PI) / 180);
//       return Math.round(areaInSquareMeters);
//     }
//   } catch (error) {
//     console.error('Error calculating area:', error);
//   }
//   return 0;
// };

function SiteModern() {
  const { selectedOrgId } = useOrg();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreateTasks = can('Task Management', 'taskAssignment', 'create');
  const [refreshSignal, setRefreshSignal] = useState(0);

  const [serviceTypes, setServiceTypes] = useState([]);
  const [viewingSite, setViewingSite] = useState(null);
  const [viewMapOpen, setViewMapOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // const [generatingGrids, setGeneratingGrids] = useState({});

  // Sub-site management state
  const [subSiteDialogOpen, setSubSiteDialogOpen] = useState(false);
  const [managingSite, setManagingSite] = useState(null);
  const [subSites, setSubSites] = useState([]);
  const [subSiteLoading, setSubSiteLoading] = useState(false);
  const [deletingSubSite, setDeletingSubSite] = useState({});
  const [viewSubSites, setViewSubSites] = useState([]); // Sub-sites for map view overlay

  // Sub-site creation state
  const [subSiteCreateOpen, setSubSiteCreateOpen] = useState(false);
  const [subSiteFormName, setSubSiteFormName] = useState('');
  const [subSiteFormGeometry, setSubSiteFormGeometry] = useState(null);
  const [creatingSubSite, setCreatingSubSite] = useState(false);

  // Sub-site edit state
  const [subSiteEditOpen, setSubSiteEditOpen] = useState(false);
  const [editingSubSite, setEditingSubSite] = useState(null);
  const [updatingSubSite, setUpdatingSubSite] = useState(false);

  // Site assessment state
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [assessingSite, setAssessingSite] = useState(null);

  // Create task state
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [createTaskSiteId, setCreateTaskSiteId] = useState(null);
  const [attentionBySite, setAttentionBySite] = useState({}); // site_id -> { missed, unassigned }

  // V7: attention badge column — one bulk agenda fetch, grouped client-side (no per-row calls)
  useEffect(() => {
    if (!selectedOrgId) return;
    let cancelled = false;
    (async () => {
      try {
        const user = getAuth().currentUser;
        const token = user ? await user.getIdToken() : null;
        const res = await axios.get(`${BASE_URL}/api/maintenance-agenda`, {
          params: { organisation_id: selectedOrgId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const map = {};
        const rows = [...(res.data?.groups?.overdue || []), ...(res.data?.groups?.unassigned || [])];
        rows.forEach(v => {
          const entry = map[v.site_id] || { missed: 0, unassigned: 0 };
          if (v.outcome === 'missed') entry.missed += 1;
          if (!v.assigned_to) entry.unassigned += 1;
          map[v.site_id] = entry;
        });
        setAttentionBySite(map);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedOrgId, refreshSignal]);

  // Manage sub-sites for a site
  const handleManageSubSites = async (site) => {
    setManagingSite(site);
    setSubSiteDialogOpen(true);
    setSubSiteLoading(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      const response = await axios.get(
        `${BASE_URL}/api/sites/${site.site_id}/sub-sites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubSites(response.data);
    } catch (error) {
      console.error('Error fetching sub-sites:', error);
      toast.error('Failed to load plots');
    } finally {
      setSubSiteLoading(false);
    }
  };

  const handleDeleteSubSite = async (subSiteId) => {
    if (!window.confirm('Delete this plot? All grids and tasks linked to it will also be deleted.')) return;
    setDeletingSubSite(prev => ({ ...prev, [subSiteId]: true }));
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      await axios.delete(`${BASE_URL}/api/sub-sites/${subSiteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubSites(prev => prev.filter(ss => ss.sub_site_id !== subSiteId));
      toast.success('Plot deleted');
    } catch (error) {
      console.error('Error deleting sub-site:', error);
      toast.error(error.response?.data?.error || 'Failed to delete plot');
    } finally {
      setDeletingSubSite(prev => ({ ...prev, [subSiteId]: false }));
    }
  };

  const handleCreateSubSite = async () => {
    if (!subSiteFormName.trim()) { toast.error('Plot name is required'); return; }
    if (!subSiteFormGeometry) { toast.error('Please draw the plot boundary'); return; }
    setCreatingSubSite(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      const response = await axios.post(
        `${BASE_URL}/api/sites/${managingSite.site_id}/sub-sites`,
        { name: subSiteFormName.trim(), geometry: subSiteFormGeometry.geometry, area: subSiteFormGeometry.area },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubSites(prev => [...prev, response.data.sub_site]);
      setSubSiteCreateOpen(false);
      setSubSiteFormName('');
      setSubSiteFormGeometry(null);
      toast.success('Plot created');
    } catch (error) {
      console.error('Error creating sub-site:', error);
      toast.error(error.response?.data?.error || 'Failed to create plot');
    } finally {
      setCreatingSubSite(false);
    }
  };

  const handleOpenEditSubSite = (ss) => {
    setEditingSubSite(ss);
    setSubSiteFormName(ss.name);
    setSubSiteFormGeometry(null); // reset; existing geometry shown via existingGeometry prop
    setSubSiteEditOpen(true);
  };

  const handleUpdateSubSite = async () => {
    if (!subSiteFormName.trim()) { toast.error('Plot name is required'); return; }
    setUpdatingSubSite(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      // Use redrawn geometry if available, otherwise stringify existing GeoJSON back to WKT
      const geometryToSend = subSiteFormGeometry
        ? subSiteFormGeometry.geometry
        : wellknown.stringify(editingSubSite.geometry);
      const areaToSend = subSiteFormGeometry
        ? subSiteFormGeometry.area
        : editingSubSite.area;
      const response = await axios.put(
        `${BASE_URL}/api/sub-sites/${editingSubSite.sub_site_id}`,
        { name: subSiteFormName.trim(), geometry: geometryToSend, area: areaToSend },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubSites(prev => prev.map(ss =>
        ss.sub_site_id === editingSubSite.sub_site_id ? response.data.sub_site : ss
      ));
      setSubSiteEditOpen(false);
      setEditingSubSite(null);
      setSubSiteFormName('');
      setSubSiteFormGeometry(null);
      toast.success('Plot updated');
    } catch (error) {
      console.error('Error updating sub-site:', error);
      toast.error(error.response?.data?.error || 'Failed to update plot');
    } finally {
      setUpdatingSubSite(false);
    }
  };

  // // Generate grids for a site
  // const handleGenerateGrids = async (siteId, siteName) => {
  //   if (!window.confirm(`Generate grid cells for site "${siteName}"?\n\nThis will create 100m x 100m grid squares for task management.`)) {
  //     return;
  //   }
  //   setGeneratingGrids(prev => ({ ...prev, [siteId]: true }));
  //   try {
  //     const user = getAuth().currentUser;
  //     const token = await user.getIdToken();
  //     const response = await axios.post(
  //       `${BASE_URL}/api/sites/${siteId}/generate-grids`,
  //       { grid_size_meters: 100 },
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     toast.success(
  //       `✅ Generated ${response.data.grid_count} grids for ${siteName}`,
  //       { position: 'top-center', autoClose: 3000 }
  //     );
  //   } catch (error) {
  //     console.error('Error generating grids:', error);
  //     toast.error(
  //       error.response?.data?.error || 'Failed to generate grids',
  //       { position: 'top-center', autoClose: 4000 }
  //     );
  //   } finally {
  //     setGeneratingGrids(prev => ({ ...prev, [siteId]: false }));
  //   }
  // };

  // Fetch service types for dropdown
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const user = getAuth().currentUser;
        const token = await user.getIdToken();
        const response = await axios.get(`${BASE_URL}/api/asset-types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setServiceTypes(response.data.map(st => ({
          value: st.id.toString(), // Ensure string for consistent comparison
          label: st.name
        })));
      } catch (error) {
        console.error('Error fetching service types:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceTypes();
  }, []);

  // Don't render until service types are loaded
  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  // Column definitions
  const columns = [
    {
      field: 'name',
      headerName: 'Site Name',
      sortable: true,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapPin size={16} color="#6C8EBF" />
          <span style={{ fontWeight: 500 }}>{value || 'N/A'}</span>
        </Box>
      )
    },
    {
      field: 'site_id',
      headerName: 'Site Code',
      sortable: true,
      renderCell: ({ value }) => (
        <Chip
          label={value || 'N/A'}
          size="small"
          variant="filled"
          sx={{ 
            fontWeight: 600,
            bgcolor: '#e3f2fd',
            color: '#47638F'
          }}
        />
      )
    },
    {
      field: 'attention',
      headerName: '',
      sortable: false,
      renderCell: ({ row }) => {
        const a = attentionBySite[row.site_id];
        const count = (a?.missed || 0) + (a?.unassigned || 0);
        if (!count) return null;
        return (
          <Chip
            label={`⚠ ${count}`}
            size="small"
            color="warning"
            onClick={(e) => { e.stopPropagation(); navigate(`/maintenance?site=${row.site_id}`); }}
            title={[a.missed ? `${a.missed} missed` : null, a.unassigned ? `${a.unassigned} unassigned` : null].filter(Boolean).join(' · ')}
          />
        );
      },
    },
    {
      field: 'asset_type_name',
      headerName: 'Service Type',
      sortable: true,
      renderCell: ({ value }) => (
        <Chip
          label={value || 'N/A'}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      )
    },
    {
      field: 'area',
      headerName: 'Area',
      sortable: true,
      align: 'right',
      renderCell: ({ value }) => {
        // Area is already stored in acres in the database
        const areaInAcres = value ? parseFloat(value).toFixed(2) : '0.00';
        return (
          <Box sx={{ fontWeight: 500, color: 'success.main' }}>
            {areaInAcres} acres
          </Box>
        );
      }
    },
    {
      field: 'address',
      headerName: 'Address',
      sortable: false,
      renderCell: ({ value }) => (
        <span style={{ color: '#666', fontSize: '0.875rem' }}>
          {value || 'No address'}
        </span>
      )
    },
    {
      field: 'sub_site_count',
      headerName: 'Plots',
      sortable: true,
      align: 'center',
      renderCell: ({ value, row }) => (
        <Chip
          label={value ?? 0}
          size="small"
          variant="outlined"
          color={value > 0 ? 'secondary' : 'default'}
          icon={<Layers size={12} />}
          onClick={(e) => { e.stopPropagation(); handleManageSubSites(row); }}
          sx={{ cursor: 'pointer', fontWeight: 600 }}
        />
      )
    },
    {
      field: 'view_map',
      headerName: 'Map',
      sortable: false,
      align: 'center',
      renderCell: ({ row }) => (
        <MuiButton
          size="small"
          startIcon={<Eye size={14} />}
          onClick={async (e) => {
            e.stopPropagation();
            setViewingSite(row);
            setViewMapOpen(true);
            // Fetch sub-sites for eco sites to show on map
            if (row.asset_type_id === 1 && row.sub_site_count > 0) {
              try {
                const user = getAuth().currentUser;
                const token = await user.getIdToken();
                const resp = await axios.get(
                  `${BASE_URL}/api/sites/${row.site_id}/sub-sites`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setViewSubSites(resp.data);
              } catch (err) {
                console.error('Error fetching sub-sites for map:', err);
                setViewSubSites([]);
              }
            } else {
              setViewSubSites([]);
            }
          }}
          sx={{ textTransform: 'none' }}
        >
          View
        </MuiButton>
      )
    },
    {
      field: 'land_type',
      headerName: 'Land Type',
      sortable: true,
      renderCell: ({ value }) => value ? (
        <Chip label={value.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} size="small" variant="outlined" />
      ) : null
    },
    {
      field: 'feasibility_status',
      headerName: 'Feasibility',
      sortable: true,
      renderCell: ({ value }) => {
        if (!value) return null;
        const colors = { draft: 'default', submitted: 'warning', approved: 'success', rejected: 'error' };
        return <Chip label={value.charAt(0).toUpperCase() + value.slice(1)} size="small" color={colors[value] || 'default'} />;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      align: 'right'
    }
  ];

  // Field definitions
  const siteFields = [
    {
      name: 'name',
      label: 'Site Name',
      type: 'text',
      required: true,
      validation: {
        required: 'Site name is required',
        minLength: { value: 2, message: 'Name must be at least 2 characters' }
      }
    },
    {
      name: 'asset_type_id',
      label: 'Service Type',
      type: 'select',
      required: true,
      validation: {
        required: 'Service type is required'
      },
      options: serviceTypes
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      multiline: true,
      rows: 2
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      multiline: true,
      rows: 2
    },
    {
      name: 'boundary',
      label: 'Site Boundary',
      type: 'map',
      singleGeometryOnly: true,
      helperText: 'Draw the site boundary on the map. Area will be calculated automatically.'
    },
    // Hidden fields to preserve geometry and area during edit
    {
      name: 'geometry',
      type: 'hidden'
    },
    {
      name: 'area',
      type: 'hidden'
    }
  ];

  return (
    <>
      <CRUDPage
        title="Site Management"
        subtitle="Manage sites and locations with map boundaries"
        icon={MapPin}
        refreshSignal={refreshSignal}
        columns={columns}
        fields={siteFields}
        searchFields={['name', 'asset_type_name', 'address']}
        permissions={{
          resource: 'Site',
          module: 'siteManagement'
        }}
        renderCustomActions={({ row, onEdit, onDelete, canEdit, canDelete }) => {
          const actions = [
            {
              label: 'View on Map',
              icon: <Eye size={16} />,
              onClick: () => {
                setViewingSite(row);
                setViewMapOpen(true);
              }
            },
            {
              label: 'Manage Plots',
              icon: <Layers size={16} />,
              onClick: () => handleManageSubSites(row)
            },
            {
              label: 'Site Assessment',
              icon: <ClipboardCheck size={16} />,
              onClick: () => { setAssessingSite(row); setAssessmentDialogOpen(true); }
            },
            ...(canCreateTasks ? [{
              label: 'Create Task',
              icon: <ListTodo size={16} />,
              onClick: () => { setCreateTaskSiteId(row.site_id); setCreateTaskOpen(true); }
            }] : []),
            // {
            //   label: generatingGrids[row.site_id] ? 'Generating...' : 'Generate Grids',
            //   icon: <Grid3x3 size={16} />,
            //   onClick: () => handleGenerateGrids(row.site_id, row.name),
            //   disabled: generatingGrids[row.site_id]
            // }
          ];
          
          // Add Edit action if user has permission
          if (canEdit && onEdit) {
            actions.push({
              label: 'Edit',
              icon: <Edit size={16} />,
              onClick: onEdit
            });
          }
          
          // Add Delete action if user has permission
          if (canDelete && onDelete) {
            actions.push({
              label: 'Delete',
              icon: <Trash2 size={16} />,
              onClick: onDelete,
              color: 'error'
            });
          }
          
          return <ActionMenu actions={actions} />;
        }}
        crudConfig={{
          endpoint: '/api/sites',
          entityName: 'Site',
          entityNamePlural: 'Sites',
          useOrgId: true,
          idField: 'site_id', // Backend uses site_id (e.g., "MECC-01-LM-7") as the primary key
          transformFetch: (fetchedData) => {
            // Add asset_type_name by matching with serviceTypes
            return fetchedData.map(site => {
              const serviceType = serviceTypes.find(st => st.value === site.asset_type_id?.toString());
              // Area is already in acres in the database, just parse it
              const areaInAcres = site.area ? parseFloat(site.area) : 0;
              
              // Backend returns site_id as the code (e.g., "MECC-01-LM-8")
              return {
                ...site,
                id: site.site_id, // Use site_id as the primary identifier
                site_id: site.site_id, // site_id IS the site code for display
                asset_type_name: serviceType ? serviceType.label : 'N/A',
                asset_type_id: site.asset_type_id?.toString() || '', // Ensure string
                area: areaInAcres // Area already in acres from database
              };
            });
          },
          transformForEdit: (data) => ({
            ...data,
            asset_type_id: data.asset_type_id?.toString() || '',
            geometry: data.geometry || '',
            area: data.area || 0
          }),
          transformCreate: (formData) => {
            if (!formData.geometry) {
              toast.error('Please draw a boundary on the map');
              throw new Error('No boundary drawn');
            }

            // Convert WKT to GeoJSON if needed
            let geometryToSend = formData.geometry;
            try {
              // Try to parse as WKT and convert to GeoJSON
              const geojson = wellknown.parse(formData.geometry);
              if (geojson) {
                geometryToSend = JSON.stringify(geojson); // Backend might expect GeoJSON as string
              }
            } catch (e) {
              // If parsing fails, use as is
              console.log('Geometry is not WKT, using as-is');
            }

            const payload = {
              name: formData.name,
              asset_type_id: parseInt(formData.asset_type_id, 10), // Convert back to number for API
              description: formData.description || '',
              address: formData.address || '',
              geometry: geometryToSend,
              area: formData.area,
              organisation_id: selectedOrgId,
              lat: 0, // Will be calculated by backend from geometry
              lng: 0
            };

            console.log('Creating site with payload:', payload); // Debug log
            return payload;
          },
          transformUpdate: (formData, id) => {
            console.log('Updating site with ID:', id, 'formData:', formData);
            
            // If boundary was redrawn, use new values, otherwise use preserved values
            let geometryToUse = formData.boundary?.geometry || formData.geometry;
            let areaToUse = formData.boundary?.area;
            
            console.log('Initial geometry:', geometryToUse);
            console.log('Initial area from boundary:', areaToUse);
            console.log('Has boundary been redrawn?', !!formData.boundary);
            
            // Backend expects GeoJSON, but we store as WKT
            // If geometry is WKT string, convert to GeoJSON
            if (typeof geometryToUse === 'string') {
              try {
                const geojson = wellknown.parse(geometryToUse);
                console.log('Converted WKT to GeoJSON:', geojson);
                
                // If boundary wasn't redrawn, recalculate area from existing geometry
                if (!formData.boundary && geojson) {
                  areaToUse = turf.area(geojson);
                  console.log('Recalculated area from existing geometry:', areaToUse, 'm²');
                }
                
                geometryToUse = geojson;
              } catch (error) {
                console.error('Error parsing WKT geometry:', error);
              }
            } else if (geometryToUse && !formData.boundary) {
              // Geometry is already GeoJSON object, calculate area
              try {
                areaToUse = turf.area(geometryToUse);
                console.log('Calculated area from GeoJSON geometry:', areaToUse, 'm²');
              } catch (error) {
                console.error('Error calculating area from GeoJSON:', error);
              }
            }
            
            // Backend requires: name, asset_type_id, geometry (validated at line 716 in server.js)
            // Backend accepts: description, address, area (optional)
            const payload = {
              name: formData.name,
              asset_type_id: parseInt(formData.asset_type_id, 10),
              description: formData.description || '',
              address: formData.address || '',
              geometry: geometryToUse, // REQUIRED by backend (as GeoJSON)
              area: areaToUse // Backend expects m² calculated from geometry
            };

            console.log('Final update payload - area:', areaToUse, 'geometry type:', typeof geometryToUse);
            return payload;
          }
        }}
        emptyMessage="No sites found. Create your first site by drawing a boundary on the map."
      />

      {/* Sub-Sites Management Dialog */}
      <Dialog
        open={subSiteDialogOpen}
        onClose={() => setSubSiteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Layers size={20} color="#9c27b0" />
            <Typography variant="h6" component="span">
              Plots: {managingSite?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => setSubSiteDialogOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {subSiteLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : subSites.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No plots found for this site.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Plot ID</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell align="right"><strong>Area (acres)</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subSites.map(ss => (
                  <TableRow key={ss.sub_site_id}>
                    <TableCell>
                      <Chip label={ss.sub_site_id} size="small" variant="outlined" color="secondary" />
                    </TableCell>
                    <TableCell>{ss.name}</TableCell>
                    <TableCell align="right">
                      {ss.area_acres != null ? `${parseFloat(ss.area_acres).toLocaleString(undefined, { maximumFractionDigits: 2 })} ac` : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit plot">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditSubSite(ss)}
                        >
                          <Edit size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete plot">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deletingSubSite[ss.sub_site_id]}
                          onClick={() => handleDeleteSubSite(ss.sub_site_id)}
                        >
                          {deletingSubSite[ss.sub_site_id] ? <CircularProgress size={16} /> : <Trash2 size={16} />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton
            startIcon={<Plus size={16} />}
            variant="contained"
            color="secondary"
            onClick={() => {
              setSubSiteFormName('');
              setSubSiteFormGeometry(null);
              setSubSiteCreateOpen(true);
            }}
          >
            Add Plot
          </MuiButton>
          <MuiButton onClick={() => setSubSiteDialogOpen(false)}>Close</MuiButton>
        </DialogActions>
      </Dialog>

      {/* Sub-Site Creation Dialog */}
      <Dialog
        open={subSiteCreateOpen}
        onClose={() => setSubSiteCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Plus size={20} color="#9c27b0" />
            <Typography variant="h6" component="span">
              New Plot — {managingSite?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => setSubSiteCreateOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
          <TextField
            label="Plot Name"
            value={subSiteFormName}
            onChange={e => setSubSiteFormName(e.target.value)}
            fullWidth
            required
            placeholder="e.g. Zone A, North Section"
          />
          <MapDrawField
            value={subSiteFormGeometry}
            onChange={setSubSiteFormGeometry}
            label="Plot Boundary"
            parentBoundary={managingSite?.geometry}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setSubSiteCreateOpen(false)} disabled={creatingSubSite}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            color="secondary"
            onClick={handleCreateSubSite}
            disabled={creatingSubSite || !subSiteFormName.trim() || !subSiteFormGeometry}
            startIcon={creatingSubSite ? <CircularProgress size={16} /> : <Plus size={16} />}
          >
            Create Plot
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Sub-Site Edit Dialog */}
      <Dialog
        open={subSiteEditOpen}
        onClose={() => { setSubSiteEditOpen(false); setEditingSubSite(null); setSubSiteFormName(''); setSubSiteFormGeometry(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit size={20} color="#9c27b0" />
            <Typography variant="h6" component="span">
              Edit Plot — {editingSubSite?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => { setSubSiteEditOpen(false); setEditingSubSite(null); setSubSiteFormName(''); setSubSiteFormGeometry(null); }}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
          <TextField
            label="Plot Name"
            value={subSiteFormName}
            onChange={e => setSubSiteFormName(e.target.value)}
            fullWidth
            required
          />
          <MapDrawField
            value={subSiteFormGeometry}
            onChange={setSubSiteFormGeometry}
            label="Plot Boundary"
            existingGeometry={editingSubSite?.geometry}
            parentBoundary={managingSite?.geometry}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => { setSubSiteEditOpen(false); setEditingSubSite(null); setSubSiteFormName(''); setSubSiteFormGeometry(null); }}
            disabled={updatingSubSite}
          >
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            color="secondary"
            onClick={handleUpdateSubSite}
            disabled={updatingSubSite || !subSiteFormName.trim()}
            startIcon={updatingSubSite ? <CircularProgress size={16} /> : <Edit size={16} />}
          >
            Save Plot
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* View Map Dialog */}
      <Dialog
        open={viewMapOpen}
        onClose={() => setViewMapOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            height: '85vh',
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2
        }}>
          <Box sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            Site Map: {viewingSite?.name}
          </Box>
          <IconButton onClick={() => setViewMapOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {viewingSite?.geometry && (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <MapboxMap
                geojson={viewingSite.geometry}
                subSites={viewSubSites}
                isFormView={true}
                mapId={`site-view-map-${viewingSite.id}`}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Site Assessment Dialog */}
      {assessmentDialogOpen && assessingSite && (
        <SiteAssessmentDialog
          open={assessmentDialogOpen}
          site={assessingSite}
          onClose={() => { setAssessmentDialogOpen(false); setAssessingSite(null); }}
          onSaved={() => {
            setAssessmentDialogOpen(false);
            setAssessingSite(null);
            setRefreshSignal((n) => n + 1); // refresh table so land/feasibility chips update
          }}
        />
      )}

      {/* Create Task Dialog (site prefilled) */}
      <CreateTaskDialog
        open={createTaskOpen}
        onClose={() => { setCreateTaskOpen(false); setCreateTaskSiteId(null); }}
        onCreated={() => { setCreateTaskOpen(false); setCreateTaskSiteId(null); }}
        prefillSiteId={createTaskSiteId}
      />
    </>
  );
}

export default SiteModern;
